// src/pages/CostEstimatorPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import DocLanguageSelect from "../components/DocLanguageSelect";
import { generateCaseEstimate } from "../utils/estimateApiClient";
import { useAuth } from "../context/AuthContext";

/** =========================
 *  Small-screen detector
 *  ========================= */
function useIsSmallScreen(breakpoint = 640) {
  const getInitial = () =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false;
  const [isSmall, setIsSmall] = useState(getInitial());
  useEffect(() => {
    const onResize = () => setIsSmall(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line
  }, []);
  return isSmall;
}

/** =========================
 *  Tooltip Helpers (mobile-safe, clamped to FORM width)
 *  ========================= */
const isTouchDevice = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || (navigator && navigator.maxTouchPoints > 0));

const HelpIcon = ({ onClick, onMouseEnter, onMouseLeave, label }) => (
  <button
    type="button"
    aria-label={"Help: " + label}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 20,
      height: 20,
      borderRadius: 6,
      border: "1px solid #c7d2fe",
      background: "#eef2ff",
      color: "#4338ca",
      cursor: "pointer",
      fontSize: 12,
      lineHeight: 1,
      marginLeft: 6,
      position: "relative",
      userSelect: "none",
    }}
  >
    ?
  </button>
);

const HelpBubble = ({ show, text, pos }) =>
  show ? (
    <div
      role="tooltip"
      style={{
        position: "fixed",
        zIndex: 9999,
        top: pos.top,
        left: pos.left,
        maxWidth: pos.maxWidth, // clamp to form width
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        background: "#ffffff",
        boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
        fontSize: 12,
        color: "#374151",
        lineHeight: 1.45,
        wordWrap: "break-word",
        whiteSpace: "normal",
      }}
    >
      {text}
    </div>
  ) : null;

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const HelpTip = ({ label, text }) => {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({
    top: 0,
    left: 0,
    maxWidth: "calc(100vw - 24px)",
  });

  // Compute a clamped position for the bubble INSIDE the form container
  const positionBubble = () => {
    if (!rootRef.current || typeof window === "undefined") return;

    const rect = rootRef.current.getBoundingClientRect();
    const formEl = rootRef.current.closest("[data-form-root='true']");
    const containerRect = formEl
      ? formEl.getBoundingClientRect()
      : { left: 12, right: window.innerWidth - 12 };
    const containerWidth = containerRect.right - containerRect.left;

    const bubbleWidth = 260;
    const margin = 8;
    const top = rect.bottom + margin;

    const minLeft = containerRect.left + margin;
    const maxLeft = containerRect.right - bubbleWidth - margin;

    const left = clamp(
      rect.left - bubbleWidth / 2 + rect.width / 2,
      minLeft,
      Math.max(minLeft, maxLeft)
    );

    setPos({ top, left, maxWidth: containerWidth - margin * 2 + "px" });
  };

  useEffect(() => {
    if (!open) return;
    positionBubble();
    const closeOnOutside = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleScroll = () => positionBubble();
    const handleResize = () => positionBubble();

    document.addEventListener("mousedown", closeOnOutside, true);
    document.addEventListener("touchstart", closeOnOutside, true);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", closeOnOutside, true);
      document.removeEventListener("touchstart", closeOnOutside, true);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, [open]);

  const touchMode = isTouchDevice();

  const handleClick = () => setOpen((v) => !v);
  const handleEnter = touchMode ? undefined : () => setOpen(true);
  const handleLeave = touchMode ? undefined : () => setOpen(false);

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <HelpIcon
        label={label}
        onClick={handleClick}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      />
      <HelpBubble show={open} text={text} pos={pos} />
    </div>
  );
};

/** =========================
 *  UI HELPERS (consistent widths)
 *  ========================= */
const LABEL_W = 210;
const FIELD_W = 280; // unified visual width fallback

const SectionTitle = ({ children }) => (
  <h3 style={{ margin: "20px 0 10px", fontSize: 18, fontWeight: 700 }}>
    {children}
  </h3>
);
const Row = ({ children, wrap }) => (
  <div
    style={{
      display: "flex",
      flexWrap: wrap ? "wrap" : "nowrap",
      gap: 12,
      alignItems: "center",
      margin: "10px 0",
      overflow: "visible",
      position: "relative",
    }}
  >
    {children}
  </div>
);

const FieldLabel = ({ id, children, help, required, w = LABEL_W }) => (
  <div style={{ position: "relative", width: w, fontWeight: 600, lineHeight: 1.2 }}>
    <label htmlFor={id} style={{ fontWeight: 600, display: "block", paddingRight: 24 }}>
      {children}
      {required ? "\u00A0*" : ""}
    </label>
    {help ? (
      <div style={{ position: "absolute", top: 0, right: 0 }}>
        <HelpTip label={children} text={help} />
      </div>
    ) : null}
  </div>
);

const Select = ({ style, ...props }) => {
  const { width, ...styleRest } = style || {};
  return (
    <select
      {...props}
      style={{
        width: "var(--field-w, " + FIELD_W + "px)",
        padding: "10px 12px",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        outline: "none",
        background: "#fff",
        position: "relative",
        appearance: "auto",
        boxSizing: "border-box",
        ...styleRest,
      }}
    />
  );
};

const Input = ({ style, ...props }) => {
  const { width, ...styleRest } = style || {}; // strip any incoming width
  return (
    <input
      {...props}
      style={{
        width: "var(--field-w, " + FIELD_W + "px)",
        padding: "10px 12px",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        outline: "none",
        position: "relative",
        boxSizing: "border-box",
        ...styleRest,
      }}
    />
  );
};

// Textarea styled like DocDynamicFieldsForm (unchanged)
const Textarea = (props) => (
  <textarea
    {...props}
    style={{
      width: "100%",
      maxWidth: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      background: "#fff",
      minHeight: 160,
      resize: "vertical",
      boxSizing: "border-box",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      ...props.style,
    }}
  />
);
const Help = ({ children }) => (
  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6, lineHeight: 1.4 }}>
    {children}
  </div>
);

/** =========================
 *  OPTION SETS
 *  ========================= */
const CASE_TYPES = [
  "Family Law",
  "Immigration",
  "Criminal Defense",
  "Civil Litigation",
  "Business/Corporate",
  "Personal Injury",
  "Real Estate",
  "Employment",
  "Estate/Probate",
  "Bankruptcy",
  "IP & Tech",
  "Tax",
  "Administrative/Gov",
  "Contracts & Commercial",
  "Housing/Landlord-Tenant",
  "Consumer & Privacy",
];

const SUBTYPES_BY_CASE = {
  "Family Law": [
    "Uncontested Divorce",
    "Contested Divorce",
    "Custody",
    "Support/Modification",
    "Prenup/Postnup",
    "Adoption",
    "Domestic Violence",
    "Guardianship",
    "Paternity",
    "Property Division",
  ],
  Immigration: [
    "Family Petition (I-130)",
    "Asylum",
    "Removal Defense",
    "Employment Visa",
    "Adjustment of Status",
    "Naturalization (N-400)",
    "RFE/NOID Response",
    "Waivers",
    "TPS/DACA",
  ],
  "Criminal Defense": [
    "Infraction",
    "Misdemeanor",
    "Felony",
    "DUI/DWI",
    "White Collar",
    "Juvenile",
    "Expungement/Record Relief",
    "Appeal",
  ],
  "Civil Litigation": [
    "Breach of Contract",
    "Business Tort",
    "Negligence",
    "Defamation",
    "Small Claims",
    "Class Action (plaintiff)",
    "Class Action (defense)",
    "Appeal",
  ],
  "Business/Corporate": [
    "LLC/Corp Formation",
    "Bylaws/Operating Agreement",
    "Commercial Contracts",
    "Compliance",
    "M&A",
    "Securities/Financing",
    "Corporate Governance",
    "Franchise",
  ],
  "Personal Injury": [
    "Auto Accident",
    "Premises Liability",
    "Medical Malpractice",
    "Product Liability",
    "Wrongful Death",
    "Workplace Injury (non-comp)",
    "Catastrophic Injury",
  ],
  "Real Estate": [
    "Residential Purchase/Sale",
    "Commercial Purchase/Sale",
    "Lease (Landlord)",
    "Lease (Tenant)",
    "Eviction/Unlawful Detainer",
    "Title/Boundary Dispute",
    "HOA/Condo",
    "Land Use/Zoning",
  ],
  Employment: [
    "Wage/Hour",
    "Discrimination",
    "Harassment",
    "Retaliation",
    "Wrongful Termination",
    "Non-Compete/Trade Secrets",
    "Employee Handbook/Policy",
    "Severance/Negotiation",
  ],
  "Estate/Probate": [
    "Will/Trust",
    "Probate Administration",
    "Conservatorship",
    "Guardianship (minor)",
    "Trust Litigation",
    "Estate Planning (tax-focused)",
    "POA/Healthcare Directive",
  ],
  Bankruptcy: [
    "Chapter 7",
    "Chapter 13",
    "Chapter 11 (small business)",
    "Adversary",
    "Creditor Defense",
    "Debt Settlement",
  ],
  "IP & Tech": [
    "Trademark (filing)",
    "Trademark Dispute",
    "Copyright",
    "Patent (provisional)",
    "Licensing",
    "DMCA/Platform",
    "Software/Privacy/Terms",
  ],
  Tax: [
    "Audit/Examination",
    "Collections/Installment",
    "Offer in Compromise",
    "Appeals",
    "SALT",
    "International/FBAR",
    "Entity Structuring (tax)",
  ],
  "Administrative/Gov": [
    "Professional License",
    "Benefits/Entitlements",
    "Agency Appeal",
    "Public Records/Open Meetings",
    "Regulatory Compliance",
  ],
  "Contracts & Commercial": [
    "Sales/Services Agreement",
    "NDA",
    "Licensing",
    "SaaS/Subscription",
    "Terms of Use/Privacy Policy",
    "MSA",
    "SOW",
  ],
  "Housing/Landlord-Tenant": [
    "Security Deposit",
    "Habitability/Repairs",
    "Rent Increase/Control",
    "Subsidized Housing",
    "Fair Housing",
    "Eviction Defense",
  ],
  "Consumer & Privacy": [
    "Debt Collection/FDCPA",
    "Credit Reporting/FCRA",
    "Data Breach/Notice",
    "Privacy Compliance (GDPR/CCPA)",
    "Consumer Arbitration",
  ],
};

const FORUMS = ["Local", "State", "Federal", "Immigration", "Administrative"];
const BILLING_PREFS = ["Hourly", "Flat Fee", "Contingency", "Not sure"];
const EXPERIENCE = ["Junior", "Mid", "Senior", "Specialist"];
const ROLE_MIX = ["Lead only", "Lead + Paralegal", "Lead + Associate", "Lead + Associate + Paralegal"];
const URGENCY = ["Standard", "Expedited", "Emergency (<72h)"];
const COMPLEXITY = ["Low", "Medium", "High"];
const STAGES = ["Pre-filing", "Filed", "Discovery", "Motions", "Hearing/Trial", "Appeal"];
const DISCOVERY_VOL = ["None", "Light", "Moderate", "Heavy"];
const HEARINGS = ["0", "1", "2–3", "4+"];
const EXPERTS = ["None", "1", "2+"];
const OPP_PARTIES = ["1", "2–3", "4+"];
const OPP_COUNSEL = ["Unknown", "No", "Yes"];
const SETTLEMENT = ["Low", "Medium", "High", "Seeking settlement"];
const TRAVEL = ["None", "Local", "Regional", "Multi-state"];

/** =========================
 *  LOCAL STORAGE KEYS
 *  ========================= */
const LS_KEYS = {
  lastPayload: "cce:lastPayload",
  lastResult: "cce:lastResult",
  history: "cce:history", // up to 3 snapshots
  uses: "cce:uses", // number
  firstTs: "cce:firstTs", // ISO string (start of window)
};

/** =========================
 *  UTILITIES
 *  ========================= */
const clampLen = (s, max) => (s || "").slice(0, max); // no normalization/trim
const currency = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

/** =========================
 *  QUOTA: 3 runs / 7 days (client-side; bypass if logged in)
 *  ========================= */
function checkQuotaAndBump({ bypass = false } = {}) {
  if (bypass) {
    return { ok: true, remaining: Infinity, resetAt: null, bypassed: true };
  }
  const now = Date.now();
  const windowMs = 7 * 24 * 60 * 60 * 1000;

  const usesRaw = localStorage.getItem(LS_KEYS.uses);
  const firstTsRaw = localStorage.getItem(LS_KEYS.firstTs);

  let uses = parseInt(usesRaw || "0", 10);
  let firstTs = firstTsRaw ? new Date(firstTsRaw).getTime() : null;

  if (!firstTs || now - firstTs > windowMs) {
    firstTs = now;
    uses = 0;
  }

  if (uses >= 3) {
    return { ok: false, remaining: 0, resetAt: new Date(firstTs + windowMs).toISOString() };
  }

  uses += 1;
  localStorage.setItem(LS_KEYS.uses, String(uses));
  localStorage.setItem(LS_KEYS.firstTs, new Date(firstTs).toISOString());

  return { ok: true, remaining: 3 - uses, resetAt: new Date(firstTs + windowMs).toISOString() };
}

/** =========================
 *  MAIN COMPONENT
 *  ========================= */
export default function CostEstimatorPage() {
  const isSmall = useIsSmallScreen(700); // match content width
  const { currentUser } = useAuth();
  const loggedIn = !!currentUser;

  const [language, setLanguage] = useState("auto");

  // ---- Inputs ----
  // Core
  const [case_type, setCaseType] = useState("");
  const [case_subtype, setCaseSubtype] = useState("");
  // Location (now REQUIRED for city/state/country)
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [country, setCountry] = useState("");
  const [forum, setForum] = useState("");
  const [billing_pref, setBillingPref] = useState("");
  const [experience_level, setExperienceLevel] = useState("");
  const [role_mix, setRoleMix] = useState("");
  const [urgency, setUrgency] = useState("");
  const [complexity, setComplexity] = useState("");

  // Estimated hours (now split into MIN and MAX)
  const [hoursMin, setHoursMin] = useState("");
  const [hoursMax, setHoursMax] = useState("");

  // Advanced
  const [stage, setStage] = useState("");
  const [filings, setFilings] = useState([]); // array of strings
  const [discovery_volume, setDiscoveryVolume] = useState("");
  const [hearings_count, setHearingsCount] = useState("");
  const [experts, setExperts] = useState("");
  const [opposing_parties, setOpposingParties] = useState("");
  const [opposing_counsel, setOpposingCounsel] = useState("");
  const [settlement_likelihood, setSettlementLikelihood] = useState("");
  const [travel, setTravel] = useState("");
  const [interpreter_needed, setInterpreterNeeded] = useState("No");
  const [interpreter_language, setInterpreterLanguage] = useState("");

  // Free text
  const [key_details, setKeyDetails] = useState("");

  // Consents
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Representation (not shown on this page, but logic honored)
  const [rep_status] = useState("");

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const resultRef = useRef(null);

  // Restore last payload on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_KEYS.lastPayload);
      if (saved) {
        const p = JSON.parse(saved);
        setLanguage(p.language ?? "auto");
        setCaseType(p.case_type ?? "");
        setCaseSubtype(p.case_subtype ?? "");
        setCity(p.city ?? "");
        setCounty(p.county ?? "");
        setStateVal(p.state ?? "");
        setCountry(p.country ?? "");
        setForum(p.forum ?? "");
        setBillingPref(p.billing_pref ?? "");
        setExperienceLevel(p.experience_level ?? "");
        setRoleMix(p.role_mix ?? "");
        setUrgency(p.urgency ?? "");
        setComplexity(p.complexity ?? "");
        // restore hours MIN/MAX separately
        setHoursMin((p.hours_min ?? "").toString());
        setHoursMax((p.hours_max ?? "").toString());
        setStage(p.stage ?? "");
        setFilings(Array.isArray(p.filings) ? p.filings : []);
        setDiscoveryVolume(p.discovery_volume ?? "");
        setHearingsCount(p.hearings_count ?? "");
        setExperts(p.experts ?? "");
        setOpposingParties(p.opposing_parties ?? "");
        setOpposingCounsel(p.opposing_counsel ?? "");
        setSettlementLikelihood(p.settlement_likelihood ?? "");
        setTravel(p.travel ?? "");
        setInterpreterNeeded(p.interpreter_needed ?? "No");
        setInterpreterLanguage(p.interpreter_language ?? "");
        setKeyDetails(p.key_details ?? "");
        setAcceptTerms(!!p.acceptTerms);
      }
      const savedResult = localStorage.getItem(LS_KEYS.lastResult);
      if (savedResult) setResult(JSON.parse(savedResult));
    } catch {}
  }, []);

  // Derived options for subtypes
  const subtypeOptions = useMemo(() => SUBTYPES_BY_CASE[case_type] || [], [case_type]);

  // Filings list by stage
  const FILING_OPTIONS = useMemo(() => {
    const common = [
      "Complaint/Petition",
      "Answer/Response",
      "Motion",
      "Brief",
      "Discovery Requests/Responses",
    ];
    if (stage === "Pre-filing") return ["Demand Letter", "Investigation", ...common];
    if (stage === "Discovery") return ["Interrogatories", "RFP/RFA", "Depositions", ...common];
    if (stage === "Motions") return ["MSJ", "MTC", "Motions in Limine", ...common];
    if (stage === "Hearing/Trial") return ["Trial Brief", "Exhibits", "Witness Lists", ...common];
    if (stage === "Appeal") return ["Notice of Appeal", "Opening Brief", "Reply Brief", ...common];
    return common;
  }, [stage]);

  const toggleFiling = (f) => {
    setFilings((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  };

  // Validate core (now includes required location fields)
  const coreValid =
    case_type &&
    forum &&
    billing_pref &&
    experience_level &&
    role_mix &&
    urgency &&
    complexity &&
    city &&
    stateVal &&
    country &&
    acceptTerms;

  // Assemble payload (keys unchanged)
  const buildPayload = () => {
    const minStr = clampLen(String(hoursMin || ""), 6);
    const maxStr = clampLen(String(hoursMax || ""), 6);
    const p = {
      language,
      // Core
      case_type,
      case_subtype,
      city: clampLen(city, 80),
      county: clampLen(county, 80),
      state: clampLen(stateVal, 40),
      country: clampLen(country, 80),
      forum,
      billing_pref,
      experience_level,
      role_mix,
      urgency,
      complexity,

      // Hours (optional): MIN and MAX sent separately
      hours_min: minStr,
      hours_max: maxStr,

      // Advanced
      stage,
      filings,
      discovery_volume,
      hearings_count,
      experts,
      opposing_parties,
      opposing_counsel,
      settlement_likelihood,
      travel,
      interpreter_needed,
      interpreter_language: interpreter_needed === "Yes" ? clampLen(interpreter_language, 40) : "",
      // Free text (1000, no normalization)
      key_details: clampLen(key_details, 1000),

      // Consents (persist locally)
      acceptTerms: !!acceptTerms,
    };
    return p;
  };

  // Save payload/result to localStorage (+ history of last 3)
  const persist = (payload, resultObj) => {
    try {
      localStorage.setItem(LS_KEYS.lastPayload, JSON.stringify(payload));
      if (resultObj) localStorage.setItem(LS_KEYS.lastResult, JSON.stringify(resultObj));
      const hist = JSON.parse(localStorage.getItem(LS_KEYS.history) || "[]");
      const next = [{ ts: new Date().toISOString(), payload, result: resultObj }, ...hist].slice(0, 3);
      localStorage.setItem(LS_KEYS.history, JSON.stringify(next));
    } catch {}
  };

  // Generate handler (calls backend; captcha handled in API client/back-end)
  const handleGenerate = async () => {
    setError("");

    if (!coreValid) {
      setError("Please complete the required fields (including Location) and accept the Terms & Privacy.");
      setResult(null);
      return;
    }

    // Quota gate (bypass if logged in)
    const quota = checkQuotaAndBump({ bypass: loggedIn });
    if (!quota.ok) {
      setError("You’ve reached 3 free estimates in the last 7 days. Please create a free account to continue.");
      setResult(null);
      return;
    }

    const payload = buildPayload();
    persist(payload, null); // save inputs immediately

    try {
      setIsGenerating(true);
      const data = await generateCaseEstimate(payload);
      setResult(data);
      persist(payload, data);

      requestAnimationFrame(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (e) {
      setResult(null);
      setError(e?.message || "Failed to generate estimate.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Clear all
  const handleReset = () => {
    setCaseType("");
    setCaseSubtype("");
    setCity("");
    setCounty("");
    setStateVal("");
    setCountry("");
    setForum("");
    setBillingPref("");
    setExperienceLevel("");
    setRoleMix("");
    setUrgency("");
    setComplexity("");
    setHoursMin("");
    setHoursMax("");
    setStage("");
    setFilings([]);
    setDiscoveryVolume("");
    setHearingsCount("");
    setExperts("");
    setOpposingParties("");
    setOpposingCounsel("");
    setSettlementLikelihood("");
    setTravel("");
    setInterpreterNeeded("No");
    setInterpreterLanguage("");
    setKeyDetails("");
    setAcceptTerms(false);
    setResult(null);
    setError("");
  };

  /** =========================
   *  Render
   *  ========================= */
  return (
    <div
      data-form-root="true"
      style={{
        maxWidth: "700px",
        margin: "24px auto",
        padding: "24px",
        border: "1px solid #ccc",
        borderRadius: "10px",
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        overflow: "visible",
        /** SURGICAL FIX: single source of truth for field width */
        "--field-w": "280px",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Case Cost Estimator</h2>
      </div>

      {/* Core */}
      <SectionTitle>
        Core Details <span style={{ fontWeight: 400, color: "#6b7280" }}>(required)</span>
      </SectionTitle>

      <Row wrap>
        <FieldLabel id="caseType" required help="Select the legal area that best fits your matter.">
          Case Type
        </FieldLabel>
        <Select
          id="caseType"
          value={case_type}
          onChange={(e) => {
            setCaseType(e.target.value);
            setCaseSubtype("");
          }}
        >
          <option value="">Select…</option>
          {CASE_TYPES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="caseSubtype" help="Optional. Narrower category within your case type.">
          Case Sub-type
        </FieldLabel>
        <Select id="caseSubtype" value={case_subtype} onChange={(e) => setCaseSubtype(e.target.value)}>
          <option value="">Select…</option>
          {subtypeOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
          <option value="Other">Other (specify in Key Facts)</option>
        </Select>
      </Row>

      {/* Location (required) — inputs align with dropdown widths */}
      <SectionTitle>
        Location <span style={{ fontWeight: 400, color: "#6b7280" }}>(required)</span>
      </SectionTitle>

      {/* City (required) */}
      {isSmall ? (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>City *</div>
          <Input
            id="city"
            placeholder="e.g., Los Angeles"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </div>
      ) : (
        <Row>
          <FieldLabel id="city" help="City where the matter is centered." w={LABEL_W} required>
            City
          </FieldLabel>
          <Input
            id="city"
            placeholder="e.g., Los Angeles"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </Row>
      )}

      {/* County (optional) */}
      {isSmall ? (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>County/Metro</div>
          <Input
            id="county"
            placeholder="e.g., Los Angeles County"
            value={county}
            onChange={(e) => setCounty(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </div>
      ) : (
        <Row>
          <FieldLabel id="county" help="County or metro area (optional)." w={LABEL_W}>
            County/Metro
          </FieldLabel>
          <Input
            id="county"
            placeholder="e.g., Los Angeles County"
            value={county}
            onChange={(e) => setCounty(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </Row>
      )}

      {/* State/Province (required) */}
      {isSmall ? (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>State/Province *</div>
          <Input
            id="state"
            placeholder="e.g., CA"
            value={stateVal}
            onChange={(e) => setStateVal(e.target.value.toUpperCase().slice(0, 40))}
            style={{ width: FIELD_W }}
          />
        </div>
      ) : (
        <Row>
          <FieldLabel id="state" help="State/Province. Two-letter for US, freeform elsewhere." w={LABEL_W} required>
            State/Province
          </FieldLabel>
          <Input
            id="state"
            placeholder="e.g., CA"
            value={stateVal}
            onChange={(e) => setStateVal(e.target.value.toUpperCase().slice(0, 40))}
            style={{ width: FIELD_W }}
          />
        </Row>
      )}

      {/* Country (required) */}
      {isSmall ? (
        <div style={{ margin: "10px 0" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Country *</div>
          <Input
            id="country"
            placeholder="e.g., USA"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </div>
      ) : (
        <Row>
          <FieldLabel id="country" help="Country." w={LABEL_W} required>
            Country
          </FieldLabel>
          <Input
            id="country"
            placeholder="e.g., USA"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={{ width: FIELD_W }}
          />
        </Row>
      )}

      <Row wrap>
        <FieldLabel id="forum" required help="Which forum applies (Local, State, Federal, Immigration, Administrative).">
          Forum/Court
        </FieldLabel>
        <Select id="forum" value={forum} onChange={(e) => setForum(e.target.value)}>
          <option value="">Select…</option>
          {FORUMS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="billing" required help="Your preferred fee style; pick ‘Not sure’ if undecided—estimate still works.">
          Billing Preference
        </FieldLabel>
        <Select id="billing" value={billing_pref} onChange={(e) => setBillingPref(e.target.value)}>
          <option value="">Select…</option>
          {BILLING_PREFS.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="experience" required help="Experience level of the lead attorney handling the matter.">
          Experience Level
        </FieldLabel>
        <Select id="experience" value={experience_level} onChange={(e) => setExperienceLevel(e.target.value)}>
          <option value="">Select…</option>
          {EXPERIENCE.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="roleMix" required help="Who will work on the matter (lead only vs. team).">
          Attorney Role Mix
        </FieldLabel>
        <Select id="roleMix" value={role_mix} onChange={(e) => setRoleMix(e.target.value)}>
          <option value="">Select…</option>
          {ROLE_MIX.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="urgency" required help="How quickly work must begin or be completed.">
          Urgency
        </FieldLabel>
        <Select id="urgency" value={urgency} onChange={(e) => setUrgency(e.target.value)}>
          <option value="">Select…</option>
          {URGENCY.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </Select>
      </Row>

      <Row wrap>
        <FieldLabel id="complexity" required help="Overall difficulty/novelty of the matter based on facts, law, and scope.">
          Complexity
        </FieldLabel>
        <Select id="complexity" value={complexity} onChange={(e) => setComplexity(e.target.value)}>
          <option value="">Select…</option>
          {COMPLEXITY.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Row>

      {/* Estimated Hours: MIN then MAX, each aligned to dropdown width */}
      <Row wrap>
        <FieldLabel id="hoursMin" help="Optional. Enter a reasonable minimum number of hours.">
          Estimated Hours
        </FieldLabel>
        <Input
          id="hoursMin"
          type="number"
          min="0"
          step="1"
          placeholder="MIN e.g., 10"
          value={hoursMin}
          onChange={(e) => setHoursMin(e.target.value)}
          style={{ width: FIELD_W }}
        />
      </Row>
      <Row wrap>
        <FieldLabel id="hoursMax" w={LABEL_W}>
          &nbsp;
        </FieldLabel>
        <Input
          id="hoursMax"
          type="number"
          min="0"
          step="1"
          placeholder="MAX e.g., 30"
          value={hoursMax}
          onChange={(e) => setHoursMax(e.target.value)}
          style={{ width: FIELD_W }}
        />
      </Row>

      {/* Advanced */}
      <div style={{ marginTop: 10 }}>
        <button
          onClick={() => setShowAdvanced((v) => !v)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #93c5fd",
            background: "#eef2ff",
            color: "#1d4ed8",
            fontWeight: 700,
            cursor: "pointer",
          }}
          type="button"
        >
          {showAdvanced ? "Hide advanced details" : "Add advanced details (optional)"}
        </button>
      </div>

      {showAdvanced && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            background: "#fafafa",
            overflow: "visible",
          }}
        >
          <Row wrap>
            <FieldLabel id="stage" help="Where your case currently stands in the process.">
              Stage
            </FieldLabel>
            <Select id="stage" value={stage} onChange={(e) => setStage(e.target.value)}>
              <option value="">Select…</option>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Row>

          <Row wrap>
            <FieldLabel id="discovery" help="Approximate amount of material to collect/review; also general doc volume.">
              Discovery / Document Volume
            </FieldLabel>
            <Select id="discovery" value={discovery_volume} onChange={(e) => setDiscoveryVolume(e.target.value)}>
              <option value="">Select…</option>
              {DISCOVERY_VOL.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
          </Row>

          <Row wrap>
            <FieldLabel id="hearings" help="Estimated count of hearings or contested motions.">
              Hearings/Motions
            </FieldLabel>
            <Select id="hearings" value={hearings_count} onChange={(e) => setHearingsCount(e.target.value)}>
              <option value="">Select…</option>
              {HEARINGS.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </Select>
          </Row>

          <Row wrap>
            <FieldLabel id="experts" help="Whether expert witnesses or specialists are needed.">
              Experts Needed
            </FieldLabel>
            <Select id="experts" value={experts} onChange={(e) => setExperts(e.target.value)}>
              <option value="">Select…</option>
              {EXPERTS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </Select>
          </Row>

          <Row wrap>
            <FieldLabel id="oppParties" help="Total number of opposing parties.">
              Opposing Parties
            </FieldLabel>
            <Select id="oppParties" value={opposing_parties} onChange={(e) => setOpposingParties(e.target.value)}>
              <option value="">Select…</option>
              {OPP_PARTIES.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          </Row>

          <Row wrap>
            <FieldLabel id="oppCounsel" help="Whether an opposing lawyer will be involved.">
              Opposing Counsel
            </FieldLabel>
            <Select id="oppCounsel" value={opposing_counsel} onChange={(e) => setOpposingCounsel(e.target.value)}>
              <option value="">Select…</option>
              {OPP_COUNSEL.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          </Row>

          <Row wrap>
            <FieldLabel id="settlement" help="Your sense of settling vs. litigating.">
              Settlement Likelihood
            </FieldLabel>
            <Select id="settlement" value={settlement_likelihood} onChange={(e) => setSettlementLikelihood(e.target.value)}>
              <option value="">Select…</option>
              {SETTLEMENT.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Row>

          <Row wrap>
            <FieldLabel id="travel" help="Expected travel for meetings, appearances, or investigation.">
              Travel
            </FieldLabel>
            <Select id="travel" value={travel} onChange={(e) => setTravel(e.target.value)}>
              <option value="">Select…</option>
              {TRAVEL.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Row>

          <Row wrap>
            <FieldLabel id="interp" help="If you need language support for meetings/court.">
              Interpreter Needed
            </FieldLabel>
            <Select
              id="interp"
              value={interpreter_needed}
              onChange={(e) => {
                const v = e.target.value;
                setInterpreterNeeded(v);
                if (v !== "Yes") setInterpreterLanguage("");
              }}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </Select>
          </Row>

          {interpreter_needed === "Yes" && (
            <Row wrap>
              <FieldLabel id="interpLang" w={LABEL_W}>
                &nbsp;
              </FieldLabel>
              <Input
                id="interpLangInput"
                placeholder="Language (if Yes)"
                value={interpreter_language}
                onChange={(e) => setInterpreterLanguage(e.target.value)}
                style={{ width: FIELD_W }}
              />
            </Row>
          )}

          {/* Filings (checkbox list) */}
          <SectionTitle>Expected Filings</SectionTitle>
          <Help>Check any filings or documents you expect to prepare or respond to.</Help>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {FILING_OPTIONS.map((f) => (
              <label key={f} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={filings.includes(f)} onChange={() => toggleFiling(f)} />
                <span>{f}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Key facts (bounded, resizable) */}
      <SectionTitle>Key Facts & Details</SectionTitle>
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: 12,
          background: "#fafafa",
          overflow: "visible",
        }}
      >
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
          Briefly describe key facts, goals, or constraints (1000 characters max).
        </div>
        <Textarea
          rows={6}
          placeholder="e.g., Deadline in 30 days; prior settlement offer; minor children; cross-border assets."
          value={key_details}
          onChange={(e) => setKeyDetails(clampLen(e.target.value, 1000))}
          spellCheck={true}
        />
      </div>

      {/* Summary Language — full width to match textarea */}
      <div style={{ marginTop: 14, marginBottom: 6, fontWeight: 700 }}>Summary Language</div>
      <div style={{ width: "100%" }}>
        <DocLanguageSelect value={language} onChange={(val) => setLanguage(val)} defaultShowExtended={false} />
      </div>

      {/* Consent */}
      <div style={{ marginTop: 12 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
          <span>
            I agree to the{" "}
            <a href="/terms" target="_blank" rel="noreferrer">
              Terms of Use
            </a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </span>
        </label>
      </div>

      {/* Action bar */}
      <div style={{ display: "flex", justifyContent: "flex-start", gap: 8, marginTop: 18 }}>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!coreValid || isGenerating}
          style={{
            padding: "10px 16px",
            border: "none",
            borderRadius: 8,
            background: coreValid && !isGenerating ? "#0ea5e9" : "#94a3b8",
            color: "#fff",
            fontWeight: 700,
            cursor: coreValid && !isGenerating ? "pointer" : "not-allowed",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {isGenerating ? (
            <>
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: "2px solid rgba(255,255,255,0.6)",
                  borderTopColor: "transparent",
                  animation: "cce-spin 1s linear infinite",
                  display: "inline-block",
                }}
              />
              Generating…
            </>
          ) : (
            "Generate Estimate"
          )}
        </button>
        <button
          type="button"
          onClick={handleReset}
          style={{
            padding: "10px 14px",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Reset
        </button>
      </div>

      {/* Logged-in hint */}
      {loggedIn ? <Help style={{ marginTop: 8 }}>You’re signed in — usage limits are not applied.</Help> : null}

      {/* Errors */}
      {error && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#7f1d1d",
            borderRadius: 10,
          }}
        >
          {error}
        </div>
      )}

      {/* Result */}
      <div ref={resultRef} />
      {result && (
        <div
          style={{
            marginTop: 24,
            padding: 16,
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            background: "#f9fafb",
          }}
        >
          <h3 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Estimated Cost</h3>

          {isSmall ? (
            // Mobile-first: stack fee range and add-ons vertically to avoid wrap issues
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>Estimated Hourly</div>
                <div>
                  {currency(result.hourly_rate_range?.min)} – {currency(result.hourly_rate_range?.max)}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>Estimated Hours</div>
                <div>
                  {result.estimated_hours?.min} – {result.estimated_hours?.max} hrs
                </div>
              </div>

              <div style={{ fontWeight: 700, borderTop: "1px dashed #e5e7eb", paddingTop: 8 }}>Estimated Fees</div>
              <div style={{ fontWeight: 800 }}>
                {currency(result.fee_range?.min)} – {currency(result.fee_range?.max)}
              </div>

              {result.add_ons?.length > 0 && (
                <>
                  <div style={{ marginTop: 8 }}>Potential Add-ons</div>
                  <div>
                    {result.add_ons.map((a, i) => (
                      <div key={i}>
                        {a.label}: {currency(a.range?.min)} – {currency(a.range?.max)}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between" }}>
                <div>Confidence</div>
                <div>{result.confidence}</div>
              </div>
            </div>
          ) : (
            // Desktop/tablet grid (two columns)
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>Estimated Hourly</div>
              <div style={{ textAlign: "right" }}>
                {currency(result.hourly_rate_range?.min)} – {currency(result.hourly_rate_range?.max)}
              </div>

              <div>Estimated Hours</div>
              <div style={{ textAlign: "right" }}>
                {result.estimated_hours?.min} – {result.estimated_hours?.max} hrs
              </div>

              <div style={{ fontWeight: 700, borderTop: "1px dashed #e5e7eb", paddingTop: 8 }}>Estimated Fees</div>
              <div style={{ textAlign: "right", fontWeight: 800, borderTop: "1px dashed #e5e7eb", paddingTop: 8 }}>
                {currency(result.fee_range?.min)} – {currency(result.fee_range?.max)}
              </div>

              {result.add_ons?.length > 0 && (
                <>
                  <div style={{ marginTop: 8 }}>Potential Add-ons</div>
                  <div style={{ textAlign: "right", marginTop: 8 }}>
                    {result.add_ons.map((a, i) => (
                      <div key={i}>
                        {a.label}: {currency(a.range?.min)} – {currency(a.range?.max)}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div style={{ marginTop: 8 }}>Confidence</div>
              <div style={{ textAlign: "right", marginTop: 8 }}>{result.confidence}</div>
            </div>
          )}

          {/* Assumptions */}
          {Array.isArray(result.assumptions) && result.assumptions.length > 0 && (
            <>
              <SectionTitle>Assumptions</SectionTitle>
              <ul style={{ marginTop: 0 }}>
                {result.assumptions.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </>
          )}

          {/* Summary (localized, short; displayed if provided by backend) */}
          {result.summary && (
            <>
              <SectionTitle>Summary</SectionTitle>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  background: "#ffffff",
                  borderRadius: 10,
                  padding: 12,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.45,
                }}
              >
                {result.summary}
              </div>
            </>
          )}

          <Help>{result.disclaimer}</Help>
        </div>
      )}

      {/* Spinner keyframes */}
      <style>{"@keyframes cce-spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}"}</style>
    </div>
  );
}
